import React, { useEffect } from 'react'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import {
  fetchLandingPage,
} from 'actions/ContentActions'
import { TYPE_OF_PAGE_BNBL, PAGE_NAME_EWC_LANDING } from 'constants/landingPage'
import { OFFER_TYPE_CRUISE_SLUG } from 'constants/offer'
import HttpStatus from 'components/Common/HttpStatus'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import ErrorPage from 'components/Pages/ErrorPage'
import IconedCallout from 'components/Common/IconedCallout'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import PaneHeader from 'components/Common/Pane/PaneHeader'
import MarkdownRender from 'LuxKit/MarkdownRender'
import ImageHero from 'components/Common/ImageHero'
import HeadData from 'components/Common/HeadData'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Faq from 'components/Common/Faq'
import { getContentPageKey } from 'lib/url/contentUtils'
import OfferListHeading from 'components/OfferList/OfferListHeading'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import type { OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList from 'components/OfferList/OfferList'
import { FLASH_OFFERS_TYPES } from 'constants/offerList'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'

const CRUISE_EXCLUDED = [`!${OFFER_TYPE_CRUISE_SLUG}`]

const TextHeading = styled(Heading)`
  position: relative;
`

const TextDescription = styled(BodyText)`
  position: relative;
  margin-top: ${rem(32)};
`

const SameHeightPaneSm = styled(Pane)`
  margin-top: ${rem(50)};

  ${mediaQueryUp.tablet} {
    margin-top: 0;
    height: 100%;
  }
`

const StyledPaneBody = styled(PaneBody)`
  padding-top: 0;
  margin-top: 0;
`

const SectionMarkdown = styled(MarkdownRender)`
  margin-top: ${rem(30)};
  display: block;
  text-align: center;
`

const StyledGrid = styled(LayoutContainer)`
  max-width: ${rem(600)};
`

const ThreeColumnGrid = styled.div`
  display: grid;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const PaneContent = styled(Pane)`
  padding: ${rem(40)};
`

interface Props {
  content: App.ContentBNBLPage
  error?: any
}

const listFilters: App.OfferListFilters = {
  offerTypes: FLASH_OFFERS_TYPES,
  holidayTypes: CRUISE_EXCLUDED,
  frontPageVisibleOnly: true,
}

function EWCLandingPageContainer(props: Props) {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchLandingPage(PAGE_NAME_EWC_LANDING, TYPE_OF_PAGE_BNBL))
  }, [dispatch])

  if (props.error) {
    if (props.error.status == 404) {
      return (
        <HttpStatus code={404}>
          <Error404PageWithOffers />
        </HttpStatus>
      )
    } else {
      return <ErrorPage error={props.error} />
    }
  }

  if (!props.content) {
    return <LoadingIndicator />
  }

  const {
    header,
    empowerment,
    howItWorks,
    faq,
    offerList,
    backgroundColour,
  } = props.content

  const extras: Array<OfferListV2Item> = [
    {
      id: 'heading',
      position: 'start',
      element: <OfferListHeading title={offerList.title} />,
      show: 'hasOffers',
    },
  ]

  return (
    <>
      <HeadData title={header.metaTitle || header.title} description={header.metaDescription || header.subTitle} />
      <ImageHero
        alt={header.title}
        aria-hidden="true"
        brightness={-15}
        gravity="auto"
        mobileAspectRatio="8:9"
        tabletAspectRatio="2:1"
        desktopAspectRatio="8:3"
        largeDesktopAspectRatio="3:1"
        id={header.image}
      >
        <StyledGrid size="sm">
          <TextHeading variant="heading1" align="center" forwardedAs="h1" colour="neutral-eight">
            {header.title}
          </TextHeading>
          <TextDescription variant="large" align="center" forwardedAs="h2" colour="neutral-eight">
            {header.subTitle}
          </TextDescription>
        </StyledGrid>
      </ImageHero>
      <PaneContent>
        <LayoutContainer size="sm">
          <BodyText variant="large" align="center" as="p">{empowerment.title}</BodyText>
        </LayoutContainer>
      </PaneContent>
      <PaneContent background={backgroundColour === 'white' ? undefined : 'none'}>
        <PaneHeader size="xs-y md">
          <Heading variant="heading3" align="center" as="h2">{howItWorks.title}</Heading>
        </PaneHeader>
        <StyledPaneBody size="xl">
          <LayoutContainer>
            <ThreeColumnGrid>
              {howItWorks.description.map(item => (
                <div key={item.title}>
                  <SameHeightPaneSm>
                    <PaneBody size="xl">
                      <IconedCallout
                        icon={
                          <img
                            src={item.icon.url}
                            width={70}
                            height={70}
                            alt={item.icon.title}
                          >
                          </img>
                        }
                        title={item.title}
                      />
                      <SectionMarkdown content={item.text} />
                    </PaneBody>
                  </SameHeightPaneSm>
                </div>
              ))}
            </ThreeColumnGrid>
          </LayoutContainer>
        </StyledPaneBody>
      </PaneContent>
      {faq && <Pane>
        <PaneHeader size="xl-y md">
          <Heading variant="heading3" align="center" as="h2">{faq.title}</Heading>
        </PaneHeader>
        <StyledPaneBody size="xl">
          <LayoutContainer>
            <Faq slug={faq.slug} />
          </LayoutContainer>
        </StyledPaneBody>
      </Pane>}
      <Pane background={backgroundColour === 'white' ? undefined : 'none'}>
        <PaneBody size="lg">
          <LayoutContainer>
            <VerticalSpacer gap={16}>
              <OfferList
                maxOffers={offerList.numberOfOffers}
                filters={listFilters}
                interstitials={extras}
              />
              <Group key="ewc_discover_deals" direction="horizontal" horizontalAlign="center">
                <TextButton kind="primary" to="/">
                  {offerList.btnText}
                </TextButton>
              </Group>
            </VerticalSpacer>
          </LayoutContainer>
        </PaneBody>
      </Pane>
    </>
  )
}

const key = getContentPageKey(PAGE_NAME_EWC_LANDING, TYPE_OF_PAGE_BNBL)
function mapStateToProps(state: App.State) {
  return {
    content: state.content.landingPages[key],
    error: state.content.landingPageErrors[key],
  }
}

export default connect(
  mapStateToProps,
)(EWCLandingPageContainer)
